import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Header, Footer } from "../../components/Orchestrio";

const IntegrationsPage = () => {
  return (
    <div className="min-h-screen bg-[#1E1E1E]" data-testid="integrations-service-page">
      <Header />

      {/* Header */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <Link to="/services" className="text-[#5F8FB8] text-sm hover:underline mb-6 inline-block">
            ← Back to Services
          </Link>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Integrations & Automation
          </h1>
          <p className="text-xl text-[#5F8FB8] mb-4">
            CRM, payments, analytics, email, APIs
          </p>
          <p className="text-lg text-[#9CA3AF] max-w-2xl leading-relaxed">
            Reduce manual work and operational friction.
          </p>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">The problem we solve</h2>
          <div className="max-w-2xl">
            <p className="text-[#9CA3AF] leading-relaxed mb-4">
              Your team is copying data between systems. Exporting CSVs, importing them elsewhere, 
              manually updating records in three different places.
            </p>
            <p className="text-[#9CA3AF] leading-relaxed mb-4">
              When something changes in one system, it doesn't update anywhere else. 
              Information is always slightly out of date, and nobody trusts the data anymore.
            </p>
            <p className="text-[#9CA3AF] leading-relaxed">
              The workarounds eat up hours every week—and mistakes slip through.
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">How we approach it</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold mb-2">We map your data flows</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Before building anything, we document exactly how information moves between your systems. 
                Where does data originate? Where does it need to go? What triggers the movement?
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We build for reliability</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Integrations fail. APIs go down. Records have missing fields. 
                We build error handling, logging, and alerting so you know when something breaks.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We document the logic</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Integration logic is often buried in code that nobody understands. 
                We write runbooks explaining exactly what the integration does and how to troubleshoot it.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We plan for change</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                APIs change. Your systems change. We build integrations that can be updated 
                without starting from scratch.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Integrations */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">What we commonly connect</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              "CRM systems",
              "Payment processors",
              "Email platforms",
              "Analytics tools",
              "Accounting software",
              "Inventory systems",
              "Marketing automation",
              "Custom databases",
              "E-commerce platforms",
              "Booking systems",
              "Document storage",
              "Communication tools"
            ].map((item, i) => (
              <div key={i} className="p-3 bg-[#242424] border border-[#2E2E2E] text-sm text-[#9CA3AF] text-center">
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">What you can expect</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-[#1E1E1E] border border-[#2E2E2E]">
              <h3 className="font-semibold mb-4">Deliverables</h3>
              <ul className="space-y-3">
                {[
                  "Integration architecture document",
                  "API development or configuration",
                  "Data mapping & transformation",
                  "Error handling & retry logic",
                  "Monitoring & alerting setup",
                  "Runbook documentation",
                  "Testing & validation"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                    <CheckCircle className="w-4 h-4 text-[#5F8FB8] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 bg-[#1E1E1E] border border-[#2E2E2E]">
              <h3 className="font-semibold mb-4">Timeline & Investment</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-[#6B7280] mb-1">Typical timeline</p>
                  <p className="font-semibold">2–8 weeks</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280] mb-1">Investment range</p>
                  <p className="font-semibold">£3,000 – £25,000 +VAT</p>
                </div>
                <div className="pt-4 border-t border-[#2E2E2E]">
                  <p className="text-xs text-[#6B7280]">
                    Cost depends on the systems involved, data complexity, 
                    and whether we're using existing APIs or building custom connections.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Best For */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">This is right for you if...</h2>
          <ul className="space-y-3 max-w-2xl">
            {[
              "Your team spends hours moving data between systems",
              "Information is inconsistent across your tools",
              "Manual data entry is causing errors",
              "You want to automate workflows that don't require human judgment"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-[#9CA3AF]">
                <span className="text-[#5F8FB8]">→</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div>
              <h2 className="text-2xl font-bold mb-3">Drowning in manual processes?</h2>
              <p className="text-[#9CA3AF] max-w-lg">
                Get in touch and tell us what's eating up your team's time. 
                We'll help you identify what can be automated.
              </p>
            </div>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2 flex-shrink-0">
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IntegrationsPage;
